import * as monaco from "monaco-editor";
import * as React from "react";
import { DiffEditor, Editor } from "lib/components/Editor";
import { Button, Modal } from "react-bootstrap";
import I18n from "lib/I18n";
import "../../lib/bigqueryLanguageProvider";
import { Status } from "../../constants";
import { convertBytesToReadable } from "lib/converter";
import { useEffect, useRef, useState } from "react";
import {
  FormStore,
  getStore,
  QUERY_NOTIFICATION_CLICK_EVENT,
  QueryNotificationClickDetail,
} from "../../store";
import { observer } from "mobx-react-lite";
import { QuickFixContentWidget } from "./widget";
import { Prompt } from "./prompt";
import { IDockviewPanelProps } from "dockview";
import { useSubscriber } from "bundles/BigqueryQueryViewer/lib/pubsub";
import styles from "./style.module.css";
import { VerticalSplitPane } from "../SplitPane";
import { QueryResultItem } from "../QueryResultItem";
import { registerBigqueryInlineCompletionProvider } from "../../lib/bigqueryLanguageProvider";

export const Form = observer(
  (props: IDockviewPanelProps<{ store: FormStore }>) => {
    const rootStore = getStore();
    const app = rootStore.app;
    const explorer = rootStore.explorer;
    const store = props.params.store;
    const subscriber = useSubscriber();

    const [width, setWidth] = useState(props.api.width);
    const [quickFixWidget, setQuickFixWidget] = useState<
      QuickFixContentWidget | undefined
    >(undefined);
    const [editor, setEditor] = useState<
      monaco.editor.IStandaloneCodeEditor | undefined
    >(undefined);
    const [ratio, setRatio] = useState(store.queryResult ? 0.5 : 1);
    const modelRef = useRef<monaco.editor.ITextModel | undefined>(undefined);
    const inlineCompletionDisposableRef = useRef<monaco.IDisposable>();

    useEffect(() => {
      props.api.setTitle(`${store.title}${store.isModified ? "*" : ""}`);
    }, [store.title, store.isModified]);

    useEffect(() => {
      if (store.queryResult && ratio == 1) {
        setRatio(0.5);
      }
    }, [store.queryResult]);

    const onExecuteQuery = async () => {
      if (isThresholdExceeded()) {
        store.confirming = true;
      } else {
        if (await store.executeQuery()) {
          setRatio(0.5);
        }
      }
    };

    const onExecuteQueryAndCloseConfirm = () => {
      store.executeQuery();
      store.confirming = false;
    };

    const onExecuteCommand = () => {
      if (store.status === Status.Waiting) {
        return;
      }
      onExecuteQuery();
    };

    const onSave = () => {
      if (store.savedQuery) {
        store.updateSavedQuery();
        return;
      }
      if (store.sharedQuery?.updatable) {
        store.updateSharedQuery();
        return;
      }
      explorer.savedQuery.isCreateOpen = true;
      explorer.savedQuery.form = store;
    };

    const onExport = () => {
      explorer.sharedQuery.isCreateOpen = true;
      explorer.sharedQuery.form = store;
    };

    const onConfirmClose = () => {
      store.confirming = false;
    };

    const onCreateGraph = () => {
      if (!store.queryResult) {
        return;
      }
      rootStore.createGraphFormStoreFromQueryResult(store.queryResult);
    };

    const onEditorDidMount = (
      editor: monaco.editor.IStandaloneCodeEditor,
      monacoModule: typeof monaco,
    ) => {
      setEditor(editor);
      const model = editor.getModel();
      if (model != null) {
        model.updateOptions({ tabSize: 2 });
        modelRef.current = model;
      }
      inlineCompletionDisposableRef.current?.dispose();
      inlineCompletionDisposableRef.current =
        registerBigqueryInlineCompletionProvider(
          monacoModule,
          async (completionModel, text) => {
            if (
              !store.preferences.bigqueryAICompletionEnabled ||
              modelRef.current !== completionModel
            ) {
              return "";
            }
            return (await store.completeQuery(text)) ?? "";
          },
        );
      editor.updateOptions({
        inlineSuggest: {
          enabled: true,
          showToolbar: "always",
          mode: "prefix",
          suppressSuggestions: true,
        },
      });
      const quickFixWidget = new QuickFixContentWidget(
        monacoModule,
        editor,
        () => store.finishFixEdits(),
      );
      setQuickFixWidget(quickFixWidget);
      editor.addContentWidget(quickFixWidget);

      editor.createContextKey("executeCondition", true);
      editor.addCommand(
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
        onExecuteCommand,
        "executeCondition",
      );
      editor.createContextKey("saveCondition", true);
      editor.addCommand(
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
        onSave,
        "saveCondition",
      );
      editor.focus();
    };

    const onDiffEditorDidMount = (
      editor: monaco.editor.IStandaloneDiffEditor,
    ) => {
      const model = editor.getModel();
      if (model != null) {
        model.original.updateOptions({ tabSize: 2 });
        model.modified.updateOptions({ tabSize: 2 });
      }
      editor.updateOptions({
        inlineSuggest: {
          enabled: true,
          showToolbar: "always",
          mode: "prefix",
          suppressSuggestions: true,
        },
      });
    };

    useEffect(() => {
      const disposable = subscriber.subscribe(store, (payload) => {
        if (payload == "focus") {
          if (editor) {
            editor.focus();
          }
          props.api.setActive();
        } else {
          if (editor) {
            editor.setSelection(payload);
            editor.revealLine(payload.startLineNumber);
            editor.focus();
          }
          props.api.setActive();
        }
      });
      return () => {
        disposable.dispose();
      };
    }, [editor]);

    useEffect(() => {
      const onQueryNotificationClick = (event: Event) => {
        const { queryId } = (event as CustomEvent<QueryNotificationClickDetail>)
          .detail;
        if (store.queryResult?.queryId === queryId) {
          props.api.setActive();
        }
      };
      window.addEventListener(
        QUERY_NOTIFICATION_CLICK_EVENT,
        onQueryNotificationClick,
      );
      return () => {
        window.removeEventListener(
          QUERY_NOTIFICATION_CLICK_EVENT,
          onQueryNotificationClick,
        );
      };
    }, []);

    useEffect(() => {
      const disposables = [
        props.api.onDidDimensionsChange((e) => {
          setWidth(e.width);
        }),
      ];
      if (store.savedQuery) {
        disposables.push(
          subscriber.subscribe(store.savedQuery, (payload) => {
            if (payload == "deleted") {
              props.api.close();
            }
          }),
        );
      }
      if (store.sharedQuery) {
        disposables.push(
          subscriber.subscribe(store.sharedQuery, (payload) => {
            if (payload == "deleted") {
              props.api.close();
            }
          }),
        );
      }
      if (store.queryResult) {
        disposables.push(
          subscriber.subscribe(store.queryResult, (payload) => {
            if (payload == "focus") {
              props.api.setActive();
            }
          }),
        );
      }
      return () => {
        inlineCompletionDisposableRef.current?.dispose();
        for (const disposable of disposables) {
          disposable.dispose();
        }
      };
    }, []);

    useEffect(() => {
      if (!quickFixWidget || !editor || quickFixWidget.isVisible()) {
        return;
      }
      if (store.fixEdits.length > 0) {
        editor.getAction("hideSuggestWidget")?.run();
        editor.getAction("editor.action.inlineSuggest.hide")?.run();
        quickFixWidget.show(store.fixEdits);
      } else if (store.nesEdits.length > 0) {
        editor.getAction("hideSuggestWidget")?.run();
        editor.getAction("editor.action.inlineSuggest.hide")?.run();
        quickFixWidget.show(store.nesEdits);
      } else {
        quickFixWidget.hide();
      }
    }, [store.fixEdits, store.nesEdits]);

    const renderDryrunResult = () => {
      if (store.dryrunStatus) {
        return I18n.t("bigquery_query_viewer.messages.dryrunning");
      }
      if (store.dryrunResult == null) {
        return;
      }
      const message = I18n.t("bigquery_query_viewer.messages.dryrun_result", {
        bytes: convertBytesToReadable(store.dryrunResult.dataScannedInBytes),
        cost: store.dryrunResult.cost.toPrecision(3),
      });
      if (isThresholdExceeded()) {
        return <span className="text-danger">{message}</span>;
      }
      return message;
    };

    const isThresholdExceeded = () => {
      if (store.preferences.bigqueryUserDefinedThresholdEnabled) {
        if (
          store.dryrunResult != null &&
          store.dryrunResult.dataScannedInBytes >=
            store.preferences.bigqueryUserDefinedThreshold * 1024 * 1024 * 1024 // GB
        ) {
          return true;
        }
      }
      return false;
    };

    const canExecute = () => {
      if (store.status === Status.Waiting) {
        return false;
      }
      if (!app.useBigquery) {
        return false;
      }
      if (store.preferences.bigqueryUserDefinedThresholdEnabled) {
        if (store.dryrunResult == null) {
          return false;
        }
        if (store.dryrunStatus) {
          return false;
        }
      }
      return true;
    };

    const renderConfirmModal = () => {
      let cost = 0;
      let bytes = 0;
      if (store.dryrunResult != null) {
        cost = store.dryrunResult.cost;
        bytes = store.dryrunResult.dataScannedInBytes;
      }

      return (
        <Modal show={store.confirming} onHide={onConfirmClose}>
          <Modal.Header closeButton>
            <Modal.Title>
              {I18n.t("bigquery_query_viewer.label.execute_confirm")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {I18n.t("bigquery_query_viewer.messages.execute_confirm", {
              bytes: convertBytesToReadable(bytes),
              cost: cost.toPrecision(3),
            })}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={onConfirmClose}>
              {I18n.t("bigquery_query_viewer.label.cancel")}
            </Button>
            <Button variant="primary" onClick={onExecuteQueryAndCloseConfirm}>
              {I18n.t("bigquery_query_viewer.label.execute")}
            </Button>
          </Modal.Footer>
        </Modal>
      );
    };

    return (
      <div className={styles.root}>
        <div className={styles.nav}>
          <Button
            variant="primary"
            size="sm"
            className="me-2"
            disabled={!canExecute() || store.isUserQueryEditing}
            onClick={onExecuteQuery}
          >
            {I18n.t("bigquery_query_viewer.label.execute")}
          </Button>
          <Button
            variant="info"
            size="sm"
            className="me-2"
            disabled={store.query.trim() === "" || store.isUserQueryEditing}
            onClick={onSave}
          >
            {I18n.t("bigquery_query_viewer.label.save")}
          </Button>
          <Button
            variant="light"
            size="sm"
            disabled={store.query.trim() === "" || store.isUserQueryEditing}
            onClick={onExport}
          >
            {I18n.t("bigquery_query_viewer.label.share")}
          </Button>
          <span className="text-muted ps-1" style={{ fontSize: "0.8em" }}>
            {renderDryrunResult()}
            {store.isQueryCompleting
              ? I18n.t(
                  "bigquery_query_viewer.messages.ai_completion_processing",
                )
              : ""}
            {store.isQueryFixing
              ? I18n.t("bigquery_query_viewer.messages.ai_fix_processing")
              : ""}
            {store.isQueryNesing
              ? I18n.t("bigquery_query_viewer.messages.ai_nes_processing")
              : ""}
          </span>
        </div>
        <VerticalSplitPane
          minRatio={0}
          maxRatio={1}
          ratio={ratio}
          onChangeRatio={(ratio) => setRatio(ratio)}
        >
          <div className={styles.editorArea}>
            <div
              className={styles.editor}
              style={{
                display: store.isUserQueryEditing ? "none" : "block",
              }}
            >
              <Editor
                height="100%"
                width={width}
                language="bigquery"
                theme="bigquery-light"
                value={store.query}
                markers={store.markers}
                onChange={(newValue) => (store.query = newValue)}
                onEditorDidMount={onEditorDidMount}
              />
            </div>
            <div
              className={styles.editor}
              style={{
                display: store.isUserQueryEditing ? "block" : "none",
              }}
            >
              <DiffEditor
                height="100%"
                width={width}
                language="bigquery"
                theme="bigquery-light"
                original={store.query}
                modified={store.editDraft}
                onChange={(newValue) => (store.editDraft = newValue)}
                onEditorDidMount={onDiffEditorDidMount}
              />
            </div>
            {store.preferences.bigqueryAICompletionEnabled && (
              <Prompt store={store} width={Math.min(width / 2, 800)} />
            )}
          </div>
          <div
            className={styles.queryResult}
            style={{ display: store.queryResult ? "block" : "none" }}
          >
            {store.queryResult && (
              <QueryResultItem
                queryResult={store.queryResult}
                onCreateGraph={onCreateGraph}
              />
            )}
          </div>
        </VerticalSplitPane>
        {renderConfirmModal()}
      </div>
    );
  },
);
